import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import {
  BrandLogo,
  ButtonLink,
  Card,
  Message,
  Page,
  Shell,
  Title
} from '../components/PageTheme';

const DemoPage = styled(Page)`
  display: grid;
  align-content: center;
  min-height: 100dvh;
  box-sizing: border-box;
`;

const DemoShell = styled(Shell)`
  display: grid;
  gap: 1.35rem;
`;

const DemoHeader = styled.header`
  display: grid;
  gap: 1rem;
`;

const DemoTitle = styled(Title)`
  max-width: 48rem;
  font-size: clamp(2.75rem, 10vw, 6rem);
`;

const DemoCard = styled(Card)`
  display: grid;
  gap: 1.25rem;
`;

const Steps = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const Step = styled.article`
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr);
  gap: 0.9rem;
  align-items: flex-start;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.12);
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(251, 191, 36, 0.08)),
    rgba(255, 255, 255, 0.05);

  @media (max-width: 520px) {
    grid-template-columns: 2.55rem minmax(0, 1fr);
    padding: 0.85rem;
  }
`;

const StepNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #fbbf24, #fb923c);
  color: #111827;
  font-size: 1.15rem;
  font-weight: 950;
  box-shadow: 0 16px 34px rgba(251, 146, 60, 0.22);

  @media (max-width: 520px) {
    width: 2.55rem;
    height: 2.55rem;
  }
`;

const StepTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: clamp(1.05rem, 3vw, 1.35rem);
  font-weight: 950;
  line-height: 1.1;
`;

const StepCopy = styled.p`
  margin: 0.35rem 0 0;
  color: rgba(226, 232, 240, 0.75);
  font-size: clamp(0.95rem, 2.4vw, 1.08rem);
  font-weight: 700;
  line-height: 1.35;
`;

const Example = styled.span`
  color: #fde68a;
  font-weight: 900;
`;

const DemoActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;
`;

const DemoButtonLink = styled(ButtonLink)`
  width: min(100%, 24rem);
`;

const Demo = () => {
  const { guestLogin } = useAuth();
  const [error, setError] = useState('');
  const [guestReady, setGuestReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const continueAsGuest = async () => {
      try {
        await guestLogin();

        if (!cancelled) {
          setGuestReady(true);
        }
      } catch (guestError) {
        console.error('Guest mode failed:', guestError);

        if (!cancelled) {
          setError(guestError.message || 'Failed to continue as guest');
        }
      }
    };

    continueAsGuest();

    return () => {
      cancelled = true;
    };
  }, [guestLogin]);

  if (!guestReady && !error) {
    return (
      <DemoPage>
        <DemoShell>
          <BrandLogo />
          <Message>Starting guest mode...</Message>
        </DemoShell>
      </DemoPage>
    );
  }

  return (
    <DemoPage>
      <DemoShell>
        <DemoHeader>
          <BrandLogo />
          <div>
            <DemoTitle>How it works.</DemoTitle>
          </div>
        </DemoHeader>

        {error ? (
          <Message $error>{error}</Message>
        ) : (
          <DemoCard>
            <Steps aria-label="How the demo works">
              <Step>
                <StepNumber>1</StepNumber>
                <div>
                  <StepTitle>Pick a place you want to show up.</StepTitle>
                  <StepCopy>
                    Choose somewhere future-you might try to dodge, like <Example>the gym</Example>,
                    work, class, or just outside your house to wake up on time.
                  </StepCopy>
                </div>
              </Step>

              <Step>
                <StepNumber>2</StepNumber>
                <div>
                  <StepTitle>Choose the date and time.</StepTitle>
                  <StepCopy>
                    Set the deadline for when you need to make it to that location.
                  </StepCopy>
                </div>
              </Step>

              <Step>
                <StepNumber>3</StepNumber>
                <div>
                  <StepTitle>Write a consequence email.</StepTitle>
                  <StepCopy>
                    Keep it simple, like <Example>&quot;I'm too lazy to go to the gym&quot;</Example>,
                    or tell your friend to ask you for money if you skip.
                  </StepCopy>
                </div>
              </Step>

              <Step>
                <StepNumber>4</StepNumber>
                <div>
                  <StepTitle>Move your phone there.</StepTitle>
                  <StepCopy>
                    Physically bring your phone to the location to fulfill your pledge and avoid the punishment.
                  </StepCopy>
                </div>
              </Step>
            </Steps>

            <DemoActions>
              <DemoButtonLink to="/create-alarm">Try it out</DemoButtonLink>
            </DemoActions>
          </DemoCard>
        )}
      </DemoShell>
    </DemoPage>
  );
};

export default Demo;
